/**
 * GroupMessage — Modal for coaches to compose and send messages to selected clients.
 * Primary storage: Supabase `coach_messages` table. AsyncStorage as offline fallback.
 */
import { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, Modal, TouchableOpacity,
  TextInput, ScrollView, ActivityIndicator,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';
import { X, Send, CheckSquare, Square } from 'lucide-react-native';

const MESSAGES_KEY = 'coach_messages';

export interface CoachMessage {
  id: string;
  text: string;
  recipientIds: string[];
  recipientNames: string[];
  sentAt: string;
}

interface ClientOption {
  id: string;
  name: string;
}

interface Props {
  visible: boolean;
  clients: ClientOption[];
  onClose: () => void;
  onSent?: () => void;
}

export async function loadCoachMessages(): Promise<CoachMessage[]> {
  // Scope to current coach — never leak other coaches' messages
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  // Try Supabase first
  const { data, error } = await supabase
    .from('coach_messages')
    .select('*')
    .eq('coach_id', user.id)
    .order('sent_at', { ascending: false })
    .limit(200);

  if (!error && data && data.length > 0) {
    const messages: CoachMessage[] = data.map((row: any) => ({
      id: row.id,
      text: row.content,
      recipientIds: row.recipient_ids ?? [],
      recipientNames: [], // names not stored server-side; resolve from client list
      sentAt: row.sent_at,
    }));
    // Cache locally
    await AsyncStorage.setItem(MESSAGES_KEY, JSON.stringify(messages));
    return messages;
  }

  // Fallback to local
  try {
    const raw = await AsyncStorage.getItem(MESSAGES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

async function saveMessage(msg: CoachMessage, coachId: string): Promise<void> {
  // Supabase insert
  const { error } = await supabase.from('coach_messages').insert({
    id: msg.id,
    coach_id: coachId,
    content: msg.text,
    recipient_ids: msg.recipientIds,
    sent_at: msg.sentAt,
  });

  if (error) {
    console.warn('[GroupMessage] Supabase insert failed, saving locally', error.message);
  }

  // Always cache locally
  const existing = await loadCoachMessagesLocal();
  existing.unshift(msg);
  await AsyncStorage.setItem(MESSAGES_KEY, JSON.stringify(existing.slice(0, 200)));
}

async function loadCoachMessagesLocal(): Promise<CoachMessage[]> {
  try {
    const raw = await AsyncStorage.getItem(MESSAGES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export default function GroupMessage({ visible, clients, onClose, onSent }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const [text, setText] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState<'group' | 'dm' | 'broadcast'>('group');

  useEffect(() => {
    if (visible) {
      setText('');
      setSelected(new Set());
      setMode('group');
    }
  }, [visible]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === clients.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(clients.map((cl) => cl.id)));
    }
  };

  const handleSend = async () => {
    if (!text.trim()) return;
    if (mode !== 'broadcast' && selected.size === 0) return;
    setSending(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const coachId = user?.id ?? '';

      if (mode === 'broadcast') {
        // Send individual message to each client
        for (const cl of clients) {
          const msg: CoachMessage = {
            id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
            text: text.trim(),
            recipientIds: [cl.id],
            recipientNames: [cl.name],
            sentAt: new Date().toISOString(),
          };
          await saveMessage(msg, coachId);
        }
      } else {
        const recipientIds = Array.from(selected);
        const recipientNames = clients
          .filter((cl) => selected.has(cl.id))
          .map((cl) => cl.name);

        const msg: CoachMessage = {
          id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
          text: text.trim(),
          recipientIds,
          recipientNames,
          sentAt: new Date().toISOString(),
        };
        await saveMessage(msg, coachId);
      }

      onSent?.();
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <View style={styles.overlay}>
        <View style={[styles.modal, { backgroundColor: c.background, borderColor: c.border }]}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={[styles.title, { color: c.text }]}>{mode === 'dm' ? 'DIRECT MESSAGE' : mode === 'broadcast' ? 'BROADCAST' : 'GROUP MESSAGE'}</Text>
            <TouchableOpacity onPress={onClose} hitSlop={8}>
              <X size={22} color={c.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Mode toggle */}
          <View style={styles.modeRow}>
            <TouchableOpacity
              style={[styles.modeBtn, mode === 'group' && { backgroundColor: c.accent }]}
              onPress={() => { setMode('group'); setSelected(new Set()); }}
            >
              <Text style={[styles.modeBtnText, { color: mode === 'group' ? '#fff' : c.textSecondary }]}>
                Group
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modeBtn, mode === 'dm' && { backgroundColor: c.accent }]}
              onPress={() => { setMode('dm'); setSelected(new Set()); }}
            >
              <Text style={[styles.modeBtnText, { color: mode === 'dm' ? '#fff' : c.textSecondary }]}>
                Direct
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modeBtn, mode === 'broadcast' && { backgroundColor: c.accent }]}
              onPress={() => { setMode('broadcast'); setSelected(new Set()); }}
            >
              <Text style={[styles.modeBtnText, { color: mode === 'broadcast' ? '#fff' : c.textSecondary }]}>
                Broadcast
              </Text>
            </TouchableOpacity>
          </View>

          {/* Client selection */}
          {mode === 'broadcast' && (
            <Text style={[styles.dmHint, { color: c.textSecondary }]}>
              Message will be sent individually to all {clients.length} client{clients.length !== 1 ? 's' : ''}.
            </Text>
          )}
          {mode === 'group' && (
            <TouchableOpacity onPress={selectAll} style={styles.selectAllRow}>
              <Text style={[styles.selectAllText, { color: c.accent }]}>
                {selected.size === clients.length ? 'Deselect All' : 'Select All'}
              </Text>
            </TouchableOpacity>
          )}

          {mode === 'dm' && (
            <Text style={[styles.dmHint, { color: c.textSecondary }]}>Select one client:</Text>
          )}

          {mode !== 'broadcast' && <ScrollView style={styles.clientList} contentContainerStyle={{ paddingBottom: 8 }}>
            {clients.map((cl) => {
              const isSelected = selected.has(cl.id);
              return (
                <TouchableOpacity
                  key={cl.id}
                  style={[styles.clientRow, { borderColor: c.border }]}
                  onPress={() => {
                    if (mode === 'dm') {
                      // Single select for DM
                      setSelected(new Set(isSelected ? [] : [cl.id]));
                    } else {
                      toggle(cl.id);
                    }
                  }}
                  activeOpacity={0.7}
                >
                  {isSelected
                    ? <CheckSquare size={18} color={c.accent} />
                    : <Square size={18} color={c.textTertiary} />
                  }
                  <Text style={[styles.clientName, { color: c.text }]}>{cl.name}</Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>}

          {/* Message input */}
          <TextInput
            style={[styles.input, { color: c.text, borderColor: c.border, backgroundColor: c.surface }]}
            placeholder="Type your message..."
            placeholderTextColor={c.textTertiary}
            value={text}
            onChangeText={setText}
            multiline
            maxLength={500}
          />

          {/* Send button */}
          <TouchableOpacity
            style={[
              styles.sendBtn,
              { backgroundColor: text.trim() && (mode === 'broadcast' ? clients.length > 0 : selected.size > 0) ? c.accent : c.border },
            ]}
            onPress={handleSend}
            disabled={sending || !text.trim() || (mode !== 'broadcast' && selected.size === 0)}
          >
            {sending ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <View style={styles.sendRow}>
                <Send size={16} color="#fff" />
                <Text style={styles.sendText}>
                  {mode === 'broadcast'
                    ? `Broadcast to ${clients.length} client${clients.length !== 1 ? 's' : ''}`
                    : `Send to ${selected.size} client${selected.size !== 1 ? 's' : ''}`}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'flex-end',
  },
  modal: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderWidth: 1,
    borderBottomWidth: 0,
    padding: 20,
    maxHeight: '85%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: '900',
    letterSpacing: 1,
  },
  modeRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  modeBtn: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 2,
  },
  modeBtnText: {
    fontSize: 13,
    fontWeight: '700',
  },
  dmHint: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
  },
  selectAllRow: {
    marginBottom: 8,
  },
  selectAllText: {
    fontSize: 13,
    fontWeight: '700',
  },
  clientList: {
    maxHeight: 200,
  },
  clientRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  clientName: {
    fontSize: 14,
    fontWeight: '600',
  },
  input: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 12,
    fontSize: 14,
    minHeight: 80,
    marginTop: 12,
    textAlignVertical: 'top',
  },
  sendBtn: {
    borderRadius: 2,
    paddingVertical: 12,
    alignItems: 'center',
    marginTop: 12,
  },
  sendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sendText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 14,
  },
});
